import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import MenuBar from "@/components/MenuBar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Calendar, Users, MessageSquare, Send, Info } from "lucide-react";
import { AMAService } from "@/services/amaService";
import { AMA } from "@/types/ama";
import { formatDistanceToNow, format } from "date-fns";
import { nb, enUS } from "date-fns/locale";

interface QuestionFormData {
  question_text: string;
  asker_name?: string;
}

const AMAPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const locale = i18n.language === 'no' ? nb : enUS;

  const [selectedAMAId, setSelectedAMAId] = useState<string | null>(null);

  const { data: currentAMA } = useQuery({
    queryKey: ['current-ama'],
    queryFn: () => AMAService.getCurrentAMA(),
  });

  const { data: archivedAMAs } = useQuery({
    queryKey: ['archived-amas'],
    queryFn: () => AMAService.getAMAsByStatus('archived'),
  });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<QuestionFormData>();

  const submitQuestionMutation = useMutation({
    mutationFn: (data: QuestionFormData) => 
      AMAService.submitQuestion({
        ...data,
        ama_id: selectedAMAId || currentAMA?.id || '',
      }),
    onSuccess: () => {
      toast.success(t('ama.public.questionSubmitted'));
      reset();
      queryClient.invalidateQueries({ queryKey: ['ama-questions'] });
    },
    onError: () => {
      toast.error(t('ama.public.questionError'));
    },
  });

  const { data: currentQuestions } = useQuery({
    queryKey: ['ama-questions', currentAMA?.id],
    queryFn: () => AMAService.getQuestionsByAMA(currentAMA?.id || ''),
    enabled: !!currentAMA?.id,
  });

  const { data: currentAnswers } = useQuery({
    queryKey: ['ama-answers', currentAMA?.id],
    queryFn: () => AMAService.getAnswersByAMA(currentAMA?.id || ''),
    enabled: !!currentAMA?.id,
  });

  const onSubmit = (data: QuestionFormData) => {
    if (!currentAMA?.id && !selectedAMAId) {
      toast.error(t('ama.public.noActiveAMA'));
      return;
    }
    submitQuestionMutation.mutate(data);
  };

  const getStatusBadge = (status: string) => {
    if (status === 'live') {
      return (
        <Badge variant="destructive" className="flex items-center gap-1">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-current opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-current"></span>
          </span>
          {t('ama.status.live')}
        </Badge>
      );
    }
    if (status === 'planned') {
      return <Badge variant="secondary">{t('ama.status.planned')}</Badge>;
    }
    return <Badge variant="outline">{t('ama.status.archived')}</Badge>;
  };

  return (
    <div className="min-h-screen bg-background">
      <MenuBar />
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2 flex items-center gap-2">
            Dagens AMA
            <button 
              onClick={() => toast.info("AMA (Ask Me Anything) er en interaktiv spørsmål-og-svar-sesjon hvor du kan stille spørsmål til eksperter innen ulike fagområder.")}
              className="inline-flex items-center"
              type="button"
            >
              <Info className="h-5 w-5 text-gray-500 hover:text-gray-700 transition-colors" />
            </button>
            kl. 18:00
          </h1>
          <p className="text-muted-foreground">{t('ama.public.description')}</p>
        </div>

        {/* Current/Featured AMA */}
        {currentAMA && (
          <Card className="mb-8 border-2">
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {getStatusBadge(currentAMA.status)}
                    {currentAMA.is_featured && (
                      <Badge variant="default">{t('ama.public.featured')}</Badge>
                    )}
                  </div>
                  <CardTitle className="text-2xl mb-2">{currentAMA.topic}</CardTitle>
                  <CardDescription className="text-base">
                    <Users className="inline h-4 w-4 mr-1" />
                    {currentAMA.expert_name}
                    {currentAMA.expert_title && ` • ${currentAMA.expert_title}`}
                    {currentAMA.expert_company && ` • ${currentAMA.expert_company}`}
                  </CardDescription>
                </div>
                {currentAMA.expert_photo_url && (
                  <img
                    src={currentAMA.expert_photo_url}
                    alt={currentAMA.expert_name}
                    className="w-20 h-20 rounded-full object-cover"
                  />
                )}
              </div>
              
              {currentAMA.description && (
                <p className="mt-4 text-muted-foreground">{currentAMA.description}</p>
              )}

              <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {format(new Date(currentAMA.scheduled_at), 'PPP', { locale })}
                </div>
                {currentAMA.status === 'planned' && (
                  <span>
                    ({formatDistanceToNow(new Date(currentAMA.scheduled_at), {
                      addSuffix: true,
                      locale,
                    })})
                  </span>
                )}
              </div>
            </CardHeader>

            <CardContent>
              {(currentAMA.status === 'live' || currentAMA.status === 'planned') && (
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="asker_name">{t('ama.public.yourName')}</Label>
                    <Input
                      id="asker_name"
                      {...register('asker_name')}
                      placeholder={t('ama.public.namePlaceholder')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="question_text">
                      {t('ama.public.yourQuestion')} *
                    </Label>
                    <Textarea
                      id="question_text"
                      {...register('question_text', { required: true })}
                      placeholder={t('ama.public.questionPlaceholder')}
                      rows={4}
                    />
                    {errors.question_text && (
                      <p className="text-sm text-destructive">{t('common.required')}</p>
                    )}
                  </div>

                  <Button type="submit" disabled={submitQuestionMutation.isPending}>
                    <Send className="mr-2 h-4 w-4" />
                    {t('ama.public.submitQuestion')}
                  </Button>
                </form>
              )}

              {currentAMA.status === 'archived' && (
                <Button onClick={() => navigate(`/ama/${currentAMA.id}`)}>
                  {t('ama.public.viewAnswers')}
                </Button>
              )}

              {/* Show submitted questions for current AMA */}
              {currentQuestions && currentQuestions.length > 0 && (
                <div className="mt-6 pt-6 border-t">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    {t('ama.public.submittedQuestions')}
                  </h3>
                  <div className="space-y-3">
                    {currentQuestions
                      .filter(q => q.status === 'approved')
                      .map(question => {
                        const answer = currentAnswers?.find(a => a.question_id === question.id);
                        return (
                          <div key={question.id} className="p-4 bg-muted/50 rounded-lg">
                            <div className="flex items-start gap-2">
                              <div className="flex-1">
                                <p className="font-medium text-sm">
                                  {question.asker_name || t('ama.public.anonymous')}:
                                </p>
                                <p className="text-sm mt-1">{question.question_text}</p>
                                {answer && (
                                  <div className="mt-3 pl-4 border-l-2 border-primary/30">
                                    <p className="text-sm font-medium text-primary">
                                      {t('ama.public.answer')}:
                                    </p>
                                    <p className="text-sm mt-1">{answer.answer_text}</p>
                                  </div>
                                )}
                              </div>
                              <Badge variant={answer ? "default" : "secondary"}>
                                {answer ? t('ama.public.answered') : t('ama.public.pending')}
                              </Badge>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Archive */}
        <div>
          <h2 className="text-2xl font-bold mb-4">{t('ama.public.archive')}</h2>
          
          <div className="space-y-4">
            {archivedAMAs?.map((ama: AMA) => (
              <Card 
                key={ama.id}
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/ama/${ama.id}`)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <CardTitle className="text-lg">{ama.topic}</CardTitle>
                      <CardDescription>
                        <Users className="inline h-4 w-4 mr-1" />
                        {ama.expert_name}
                        {ama.expert_company && ` • ${ama.expert_company}`}
                      </CardDescription>
                      <div className="text-xs text-muted-foreground mt-2">
                        <Calendar className="inline h-3 w-3 mr-1" />
                        {format(new Date(ama.scheduled_at), 'PPP', { locale })}
                      </div>
                    </div>
                    {ama.expert_photo_url && (
                      <img
                        src={ama.expert_photo_url}
                        alt={ama.expert_name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    )}
                  </div>
                </CardHeader>
              </Card>
            ))}

            {(!archivedAMAs || archivedAMAs.length === 0) && (
              <Card>
                <CardContent className="py-12 text-center">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">
                    {t('ama.public.noArchive')}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AMAPage;
